import React, { PureComponent, Fragment } from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { store } from 'redux-rest-reducer';
import {
  getAll as getNodeList,
  post as postNodeList,
  patch as patchNodeList,
} from 'actions/nodes';
import { patch as patchDraftMaterialize } from 'actions/drafts';
import * as Sentry from '@sentry/react';

import { Grid, Divider, Portal, Button, Header, Segment, Input } from 'semantic-ui-react';
import { NODE_DEFAULT_DATA } from 'constants/node';
import NotificationPortal from 'comps/notification-portal';
import {
  BLOCK_TYPE_OPTIONS,
  SECTION_CODE_OPTIONS,
  TAB_CODE_OPTIONS,
} from 'constants/block-options';
import 'storm-react-diagrams/dist/style.min.css';

import CurriculumDropdown from './chapter-version-dropdown';
import ActivityGraph from './activity-graph';
import { SelectedBox } from './search-worksheets/selected-box';
import { deSerializeGraphNode, serializeGraphNode } from './serializer';

class CurriculumGraph extends PureComponent {
  static propTypes = {
    nodes: PropTypes.shape({}),
    actions: PropTypes.shape({}).isRequired,
  };

  constructor(props) {
    super(props);
    this.state = {
      selectedProgram: null,
      selectedChapter: null,
      selectedChapterCode: null,
      selectedActivityList: {},
      nodeData: {},
      linkData: {},
      nodeBlockData: null,
      removeNodeFn: null,
      addNodeFn: null,
      toggleGraph: false,
      draftPost: store.post(),
      draftMaterializePatch: store.patch(),
      draftData: {},
      savePortalOpen: false,
      serializableData: null,
      openSavedNotificationPortal: false,
      openConfirmationPortal: false,
      confirmationFunc: null,
      reset: false,
      isNotificationError: undefined,
      selectionBoxNodes: new Set(),
      tagModalOpen: false,
      deletePortalOpen: false,
      deletePortalNodeId: '',
      testBlockWarning: true,
      puzzleBlockWarning: true,
      warningPortalOpen: false,
      warningPortalMessage: '',
      memoizeActivityData: null,
      showNotificationPortal: false,
      originalDraftData: null,
    };

    this.isPathSame = React.createRef();
    this.addNodeFromActivity = this.addNodeFromActivity.bind(this);
    this.removeActivityAndNode = this.removeActivityAndNode.bind(this);
    this.returnSelectedList = this.returnSelectedList.bind(this);
    this.setAddRemoveAndDeleteOptionNodeFn = this.setAddRemoveAndDeleteOptionNodeFn.bind(
      this,
    );
    this.serializeGraphData = this.serializeGraphData.bind(this);
  }

  static getDerivedStateFromProps({ nodes, drafts, actions }, prevState) {
    let newState = {};
    if (drafts) {
      const draftPatch = store.patch(drafts, prevState.draftMaterializePatch.id);
      const { resolved } = draftPatch || {};

      if (resolved && resolved.isPatched) {
        newState = {
          draftMaterializePatch: draftPatch,
          openSavedNotificationPortal: true,
          showNotificationPortal: false,
          isNotificationError: false,
          notificationMessage:
            prevState.draftData.name +
            ' is saved as version ' +
            resolved.data.version +
            ' for chapter ' +
            resolved.data.name,
        };
      } else if (
        resolved &&
        resolved.isPatchingFailed &&
        !prevState.showNotificationPortal
      ) {
        const error = store.getError(resolved);
        const { message: errorMessage } = error || {};

        newState = {
          openSavedNotificationPortal: true,
          isNotificationError: true,
          notificationMessage:
            errorMessage !== undefined
              ? '\n Draft Not Saved due to error: ' + JSON.stringify(errorMessage)
              : 'Something went wrong.',
        };
      }
    }
    if (prevState.selectedChapter && nodes) {
      const nodeList = store.getAll(nodes, prevState.selectedChapter.id);
      const nodePost = store.post(nodes, prevState.draftPost.id);
      let draftData = {};
      if (nodePost.resolved && nodePost.resolved.isCreated) {
        draftData = nodePost.resolved.data;
        Object.assign(newState, {
          draftPost: nodePost,
          draftData: draftData,
          serializableData: null,
          openSavedNotificationPortal: true,
          isNotificationError: false,
          notificationMessage: 'Draft Saved as ' + draftData.name,
        });
      } else if (nodePost.resolved && nodePost.resolved.isCreatingFailed) {
        const error = store.getError(nodePost.resolved);

        if (error) {
          Object.assign(newState, {
            openSavedNotificationPortal: true,
            isNotificationError: true,
            notificationMessage:
              '\n Draft Not Saved due to error: ' + JSON.stringify(error.message),
          });
        }
      }
      let newNodeData = {};
      let newLinkData = {};
      let newSelectedActivityList = {};
      nodeList.data.sort((a, b) =>
        a.position > b.position ? 1 : b.position > a.position ? -1 : 0,
      );
      nodeList.data.forEach(node => {
        let deserialized = deSerializeGraphNode(node);
        newNodeData[node.id] = deserialized[0];
        newLinkData[node.id] = deserialized[1];
        newSelectedActivityList[node.worksheet.id] = node.worksheet;
      });
      if (
        !(
          Object.values(newNodeData).length === 0 ||
          _.isEqual(newNodeData, prevState.nodeData)
        )
      ) {
        Object.assign(newState, {
          nodeData: newNodeData,
          linkData: newLinkData,
          selectedActivityList: newSelectedActivityList,
          toggleGraph: true,
        });
      }
    }
    if (Object.keys(newState).length === 0) {
      return null;
    } else {
      return newState;
    }
  }

  setData = obj => {
    this.setState(obj);
  };

  loadData(isChapter, entity, func, obj) {
    let draft;
    if (!isChapter) {
      draft = entity;
      entity = {};
    }
    this.setState(
      {
        toggleGraph: false,
        selectedChapter: entity,
        draftData: draft,
        originalDraftData: draft,
      },
      () => func(obj),
    );
  }

  addNodeFromActivity(activityData) {
    const {
      selectedProgram,
      selectedActivityList,
      selectedChapter,
      nodeBlockData,
      testBlockWarning,
      puzzleBlockWarning,
    } = this.state;
    const newNode = Object.assign({}, NODE_DEFAULT_DATA);

    if (nodeBlockData) {
      if (
        nodeBlockData.tab_code === TAB_CODE_OPTIONS.MASTERY &&
        nodeBlockData.section_code === SECTION_CODE_OPTIONS.TESTS &&
        nodeBlockData.block_type !== BLOCK_TYPE_OPTIONS.TESTS &&
        testBlockWarning
      ) {
        Sentry.configureScope(scope => {
          Sentry.setTags({
            nodeType: `${newNode.node_type}`,
            sheetName: `${activityData.learnosity_activity_ref}`,
            conditionBreached: 'Tab code = MASTERY and Section code = TESTS',
          });
          Sentry.captureMessage('Tab code = MASTERY and Section code = TESTS', 'warning');
        });

        this.setState({
          warningPortalOpen: true,
          warningPortalMessage: `The Section code is 'TESTS' but the block code is not 'TEST'. Do you want to continue? `,
          memoizeActivityData: activityData,
        });

        return;
      }

      if (
        nodeBlockData.tab_code === TAB_CODE_OPTIONS.PUZZLE_CARDS &&
        nodeBlockData.section_code === SECTION_CODE_OPTIONS.PUZZLE_CARDS &&
        nodeBlockData.block_type !== BLOCK_TYPE_OPTIONS.PUZZLE_CARDS &&
        puzzleBlockWarning
      ) {
        Sentry.configureScope(scope => {
          Sentry.setTags({
            nodeType: `${newNode.node_type}`,
            sheetName: `${activityData.learnosity_activity_ref}`,
            conditionBreached: 'Tab code = PUZZLE_CARDS and Section code = PUZZLE_CARDS',
          });
          Sentry.captureMessage(
            'Tab code = PUZZLE_CARDS and Section code = PUZZLE_CARDS',
            'warning',
          );
        });

        this.setState({
          warningPortalOpen: true,
          warningPortalMessage: `The Tab and Section codes are 'PUZZLE CARDS' but the block code is not 'PUZZLE CARDS'. Do you want to continue?`,
          memoizeActivityData: activityData,
        });

        return;
      }

      newNode['block'] = nodeBlockData;
    }
    newNode['chapter_id'] = selectedChapter.id;
    newNode['program_id'] = selectedProgram.id;
    newNode['worksheet_id'] = activityData.id;
    newNode['is_live'] = activityData.is_live;
    newNode['points_available'] = activityData.points_available;
    newNode['position'] =
      Object.keys(selectedActivityList).map(k => ({ [k]: selectedActivityList[k] }))
        .length + 1;
    newNode['course_type'] = activityData.course_type;
    newNode['sheet_time'] = activityData.sheet_time;
    newNode['complete_in'] = activityData.complete_in;
    if (activityData.target_accuracy) {
      newNode['completionData'] = [
        [
          {
            attribute: 'accuracy',
            attributeValue: activityData.target_accuracy,
            operator: '>=',
          },
        ],
      ];
    }
    newNode['reattemptData'] = [
      [
        {
          attribute: 'accuracy',
          attributeValue: 100,
          operator: '<',
        },
      ],
    ];
    this.addNode(newNode, activityData);
  }

  addNode = (newNodeDt, newActivityData) => {
    //Create node out of activity and send down to
    let newNodeData = Object.assign({}, this.state.nodeData);
    let newSelectedActivityList = Object.assign({}, this.state.selectedActivityList);

    newSelectedActivityList[newActivityData.id] = newActivityData;

    let nodeID = newNodeDt.id;
    if (!nodeID) {
      const uuidv4 = require('uuid/v4');
      const ndID = uuidv4();
      nodeID = ndID;
    }
    newNodeDt['id'] = nodeID;
    newNodeDt['worksheet'] = newActivityData;
    newNodeData[newNodeDt.id] = newNodeDt;

    this.setState(
      {
        nodeData: newNodeData,
        selectedActivityList: newSelectedActivityList,
      },
      () => {
        this.state.addNodeFn(newNodeDt.id, newNodeDt);
      },
    );
  };

  returnSelectedList() {
    return this.state.selectedActivityList;
  }

  removeActivityAndNode(selectedNodeID) {
    const { selectedActivityList, nodeData, removeNodeFn } = this.state;
    let newSelectedActivityList = Object.assign({}, selectedActivityList);
    let newNodeData = Object.assign({}, nodeData);
    let selectedActivityID = newNodeData[selectedNodeID].worksheet.id;

    delete newSelectedActivityList[selectedActivityID];
    delete newNodeData[selectedNodeID];

    this.setState(
      {
        selectedActivityList: newSelectedActivityList,
        nodeData: newNodeData,
      },
      () => {
        this.handleDeleteNodePortal(selectedNodeID);
        removeNodeFn(selectedNodeID);
      },
    );
    // return(newSelectedActivityList);
  }

  setAddRemoveAndDeleteOptionNodeFn(addFunc, removeFunc) {
    this.setState({
      removeNodeFn: removeFunc,
      addNodeFn: addFunc,
    });
  }

  setProgram = programData => {
    this.setState({
      selectedProgram: programData,
    });
  };

  setChapterCode = chapterCode => {
    this.setState({
      selectedChapterCode: chapterCode,
    });
  };

  setChapter = (entityData, isChapter) => {
    const { getNodeList } = this.props.actions;
    if (isChapter) {
      const chapterID = entityData.id;
      this.loadData(true, entityData, getNodeList, chapterID);
    } else {
      let newNodeData = {};
      let newLinkData = {};
      let newSelectedActivityList = {};
      if (entityData.data && entityData.data.nodes && entityData.data.nodes.length > 0) {
        entityData.data.nodes.sort((a, b) =>
          a.position > b.position ? 1 : b.position > a.position ? -1 : 0,
        );
        entityData.data.nodes.forEach(node => {
          let deserialized = deSerializeGraphNode(node);
          newNodeData[node.id] = deserialized[0];
          newLinkData[node.id] = deserialized[1];
          newSelectedActivityList[node.worksheet.id] = node.worksheet;
        });
      }
      let newEntityData = Object.assign({}, entityData);
      delete newEntityData['data'];
      this.loadData(false, newEntityData, this.setData, {
        nodeData: newNodeData,
        linkData: newLinkData,
        selectedActivityList: newSelectedActivityList,
        toggleGraph: true,
        draftData: newEntityData,
        originalDraftData: entityData,
      });
    }
  };

  removeNode(nodeID) {
    this.state.removeNodeFn(nodeID);
  }

  newChapterVersion = () => {
    const { selectedChapterCode } = this.state;
    this.setState(
      {
        toggleGraph: true,
        selectedChapter: selectedChapterCode,
        nodeData: {},
        linkData: {},
        selectedActivityList: {},
        serializableData: null,
      },
      () => this.createNewDraft(),
    );
  };

  serializeGraphData(nodes, links) {
    const {
      selectedChapterCode,
      draftPost,
      draftData,
      lookupTable,
      originalDraftData,
    } = this.state;

    let chapterVersionData = Object.assign({}, selectedChapterCode);

    delete chapterVersionData['id'];
    delete chapterVersionData['latest_chapter_id'];
    delete chapterVersionData['version'];

    let draftValidation = this.validateDraft(nodes, links);

    if (draftValidation) {
      chapterVersionData['nodes'] = nodes.map((node, i) =>
        serializeGraphNode(node, links, lookupTable),
      );

      let postData = Object.assign({}, draftData);
      postData['data'] = chapterVersionData;

      this.props.actions.postNodeList(draftPost.id, postData);
    }
  }

  createNewDraft = () => {
    const {
      selectedChapterCode,
      draftPost,
      draftName,
      serializableData,
      lookupTable,
      reset,
    } = this.state;
    const { postNodeList } = this.props.actions;

    let postData = {};
    postData['chapter_code'] = selectedChapterCode.code;
    postData['program_id'] = selectedChapterCode.program_id;
    postData['name'] = draftName;

    if (serializableData) {
      const { nodes, links } = serializableData;
      let chapterVersionData = Object.assign({}, selectedChapterCode);

      delete chapterVersionData['id'];
      delete chapterVersionData['latest_chapter_id'];
      delete chapterVersionData['version'];
      let draftValidation = this.validateDraft(nodes, links);
      let startNodeId = null;
      if (draftValidation) {
        chapterVersionData['nodes'] = nodes.map((node, i) => {
          let serializedNode = serializeGraphNode(node, links, lookupTable);
          if (serializedNode && serializedNode.position === 1) {
            startNodeId = serializedNode.id;
          }
          return serializedNode;
        });
        if (reset) {
          chapterVersionData['nodes'].map(node => {
            if (node['id'] !== startNodeId) {
              node['unlock_criteria'] = {
                left: {
                  node_id: startNodeId,
                  _type: 'node',
                  attribute: 'completed',
                },
                operator: '=',
                _type: 'comparison',
                right: { value: 1, _type: 'num' },
              };
            }
            return node;
          });
        }
        postData['data'] = chapterVersionData;
        postNodeList(draftPost.id, postData);
      }
    } else {
      postData['data'] = {};
      postNodeList(draftPost.id, postData);
    }
  };

  handleSubmitDraftName = () => {
    if (this.state.serializableData) {
      this.createNewDraft();
    } else {
      this.newChapterVersion();
    }
    this.handleCloseAssignmentPortal();
  };

  handleChangeDraftName = (e, { value }) => {
    this.setState({
      draftName: value,
    });
  };

  handleCloseAssignmentPortal = () => {
    this.setState({
      savePortalOpen: false,
    });
  };

  handleCloseNotificationPortal = () => {
    this.setState({
      openSavedNotificationPortal: false,
      notificationMessage: '',
      isNotificationError: false,
      showNotificationPortal: true,
    });
  };

  handleCloseWarningPortal = () => {
    this.setState({
      warningPortalOpen: false,
      warningPortalMessage: '',
      testBlockWarning: true,
      puzzleBlockWarning: true,
    });
  };

  handleConfirmPortalWarning = fromWarningPlace => {
    const { memoizeActivityData } = this.state;

    this.setState(
      {
        warningPortalOpen: false,
        warningPortalMessage: '',
        [fromWarningPlace]: false,
      },
      () => this.addNodeFromActivity(memoizeActivityData),
    );
  };

  handleOpenPortal = ({ serializableData, reset }) => {
    if (serializableData) {
      this.setState({
        serializableData: serializableData,
        savePortalOpen: true,
        reset: reset ? true : false,
      });
    } else {
      this.setState({
        savePortalOpen: true,
      });
    }
  };

  setLookupTable = (lookupTable, func, ...args) => {
    this.setState(
      {
        lookupTable: lookupTable,
      },
      () => func(...args),
    );
  };

  validateDraft = (nodes, links) => {
    let newState = {};
    let prevPositions = [];
    let draftValid = true;
    let olderMessage;
    nodes.forEach(node => {
      if (!node.activityNodeData.block) {
        console.log('node, ', node);
        newState['openSavedNotificationPortal'] = true;
        olderMessage = newState['notificationMessage'] || '';
        newState['notificationMessage'] =
          olderMessage +
          node.activityNodeData.worksheet.learnosity_activity_ref +
          ' is not yet assigned a block \n';
        draftValid = false;
      }
      if (node.editable) {
        newState['openSavedNotificationPortal'] = true;
        olderMessage = newState['notificationMessage'] || '';
        newState['notificationMessage'] =
          olderMessage +
          node.activityNodeData.worksheet.learnosity_activity_ref +
          ' is still in edit mode \n';
        draftValid = false;
      }
      if (
        !node.activityNodeData.position ||
        prevPositions.includes(node.activityNodeData.position)
      ) {
        newState['openSavedNotificationPortal'] = true;
        olderMessage = newState['notificationMessage'] || '';
        newState['notificationMessage'] =
          olderMessage +
          node.activityNodeData.worksheet.learnosity_activity_ref +
          ' has a position clash \n';
        draftValid = false;
      }
      prevPositions.push(node.activityNodeData.position);
    });
    links.forEach(link => {
      if (link.source === link.target) {
        let foundNode = nodes.find(nd => nd.id === link.source);
        newState['openSavedNotificationPortal'] = true;
        olderMessage = newState['notificationMessage'] || '';
        newState['notificationMessage'] =
          olderMessage +
          foundNode.activityNodeData.worksheet.learnosity_activity_ref +
          ' has a self referencing link \n';
        draftValid = false;
      }
    });
    if (!draftValid) {
      this.setState(newState);
    }
    return draftValid;
  };

  finalMaterialiseDraft = () => {
    const { draftMaterializePatch, draftData } = this.state;
    const { actions, drafts } = this.props;
    const { patchDraftMaterialize } = actions || {};

    patchDraftMaterialize(draftMaterializePatch.id, draftData.id, {
      action: 'materialize',
    });

    const draftPatch = store.patch(drafts, draftMaterializePatch.id);
    const { resolved } = draftPatch || {};
    const { isPatchingFailed } = resolved || {};

    this.setState(
      {
        showNotificationPortal: isPatchingFailed && false,
      },
      () => this.handleCloseConfirmationPortal(),
    );
  };

  materialiseDraft = () => {
    this.setState({
      openConfirmationPortal: true,
      confirmationFunc: this.finalMaterialiseDraft,
      confirmationMessage:
        this.state.draftData.name + ' will be made to a new chapter version. Continue?',
    });
  };

  handleCloseConfirmationPortal = () => {
    this.setState({
      openConfirmationPortal: false,
      confirmationFunc: null,
      confirmationMessage: '',
    });
  };

  handleTagModalClose = () => {
    this.setState({ tagModalOpen: false, selectionBoxNodes: new Set() });
  };

  openTagModal = () => {
    this.setState({
      tagModalOpen: true,
    });
  };

  updateBlockData = nodeBlockData => {
    this.setState({ nodeBlockData });
  };

  handleDeleteNodePortal = nodeId => {
    this.setState({
      deletePortalOpen: true,
      deletePortalNodeId: nodeId,
    });
  };

  componentDidMount() {
    this.isPathSame.current = window.location.pathname;
    window.onbeforeunload = function() {
      return '';
    };
  }

  render() {
    const {
      nodeData,
      linkData,
      selectedActivityList,
      selectedProgram,
      selectedChapter,
      toggleGraph,
      savePortalOpen,
      draftData,
      openSavedNotificationPortal,
      openConfirmationPortal,
      notificationMessage,
      confirmationFunc,
      confirmationMessage,
      addNodeFn,
      selectedChapterCode,
      isNotificationError,
      selectionBoxNodes,
      tagModalOpen,
      nodeBlockData,
      warningPortalOpen,
      warningPortalMessage,
      testBlockWarning,
    } = this.state;

    return (
      <Fragment>
        <Portal
          onClose={this.handleCloseConfirmationPortal}
          open={openConfirmationPortal}
        >
          <Segment
            style={{
              left: '40%',
              position: 'fixed',
              top: '40%',
              zIndex: 1000,
            }}
          >
            <Header>{confirmationMessage}</Header>
            <Button content="Confirm" positive onClick={confirmationFunc} />
            <Button
              content="Cancel"
              negative
              onClick={this.handleCloseConfirmationPortal}
            />
          </Segment>
        </Portal>

        <Portal onClose={this.handleCloseAssignmentPortal} open={savePortalOpen}>
          <Segment
            style={{
              left: '40%',
              position: 'fixed',
              top: '40%',
              zIndex: 1000,
            }}
          >
            <Header>Save new draft as</Header>
            <Input placeholder="Enter Draft Name" onChange={this.handleChangeDraftName} />
            <Button content="Confirm" positive onClick={this.handleSubmitDraftName} />
            <Button
              content="Cancel"
              negative
              onClick={this.handleCloseAssignmentPortal}
            />
          </Segment>
        </Portal>

        <Portal onClose={this.handleCloseWarningPortal} open={warningPortalOpen}>
          <Segment
            style={{
              position: 'fixed',
              left: '50%',
              top: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 1000,
            }}
          >
            <Header>{warningPortalMessage}</Header>
            <br />
            <Button
              content="OK"
              negative
              onClick={() => {
                const fromWarningPlace = testBlockWarning
                  ? 'testBlockWarning'
                  : 'puzzleBlockWarning';

                return this.handleConfirmPortalWarning(fromWarningPlace);
              }}
            />
            <Button content="Cancel" positive onClick={this.handleCloseWarningPortal} />
          </Segment>
        </Portal>

        <NotificationPortal
          openSavedNotificationPortal={openSavedNotificationPortal}
          notificationMessage={notificationMessage}
          handleCloseNotificationPortal={this.handleCloseNotificationPortal}
          isError={isNotificationError}
        />
        <Grid divided="vertically" columns={2} relaxed="very" stackable>
          <Grid.Column width={5} rows={2}>
            <CurriculumDropdown
              setProgram={this.setProgram}
              setChapter={this.setChapter}
              setChapterCode={this.setChapterCode}
              selectedProgramData={selectedProgram}
              onNewChapter={this.handleOpenPortal}
            />
            <Divider />

            {Object.keys(selectedActivityList || {}).length > 0 && (
              <SelectedBox data={selectedActivityList} />
            )}
          </Grid.Column>
          <Grid.Column width={1}>
            {toggleGraph && (
              <ActivityGraph
                key={selectedChapter.id}
                nodeDataList={nodeData}
                linkDataList={linkData}
                onDeleteNode={this.removeActivityAndNode}
                onSave={this.serializeGraphData}
                setAddRemoveAndDeleteOptionNodeFn={this.setAddRemoveAndDeleteOptionNodeFn}
                setLookupTable={this.setLookupTable}
                openSaveAsPortal={this.handleOpenPortal}
                materialiseDraft={this.materialiseDraft}
                isChapter={draftData ? false : true}
                chapterCode={selectedChapterCode.code}
                programId={selectedProgram.id}
                onAddSelected={this.addNodeFromActivity}
                selectedActivityList={selectedActivityList}
                isGraphActive={!addNodeFn ? false : true}
                editReportConfig={this.openEditReportConfig}
                selectionBoxNodes={selectionBoxNodes}
                tagModalOpen={tagModalOpen}
                openTagModal={this.openTagModal}
                handleTagModalClose={this.handleTagModalClose}
                updateBlockData={this.updateBlockData}
                nodeBlockData={nodeBlockData}
              />
            )}
          </Grid.Column>
        </Grid>
      </Fragment>
    );
  }
}

export default connect(
  ({ nodes, drafts }) => ({
    nodes: nodes,
    drafts: drafts,
  }),
  dispatch => ({
    actions: bindActionCreators(
      { getNodeList, postNodeList, patchNodeList, patchDraftMaterialize },
      dispatch,
    ),
  }),
)(CurriculumGraph);
